import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/ai-incident-management", label: "AI incident management" },
  { to: "/root-cause-analysis-ai", label: "Root cause analysis AI" },
  { to: "/major-incident-management", label: "Major incident management" },
  { to: "/it-downtime-reduction", label: "IT downtime reduction" },
];

const FAQ = [
  {
    q: "What is AIOps incident reasoning?",
    a: "AIOps incident reasoning applies AI to interpret correlated signals from observability, ITSM, and on-call platforms during an incident. CREAIOps focuses on the reasoning layer: hypotheses, evidence, and approval-ready actions — rather than collecting raw telemetry.",
  },
  {
    q: "How is CREAIOps different from traditional AIOps platforms?",
    a: "Traditional AIOps tools focus on anomaly detection and event correlation. CREAIOps focuses on what those signals mean during an incident: ranked RCA hypotheses, safe next steps, and human-approved remediation runbooks across cross-functional teams.",
  },
  {
    q: "Does CREAIOps require integrating my full observability stack?",
    a: "No. CREAIOps can start with anonymized incident inputs from the on-call engineer or incident manager. Deeper integrations can be layered in over time as the team adopts the workspace.",
  },
  {
    q: "Is CREAIOps safe to use during a live major incident?",
    a: "CREAIOps is advisory. It never executes changes. Engineers retain full control of remediation. The platform produces structured reasoning and human-reviewable artefacts that speed up decisions without bypassing governance.",
  },
  {
    q: "Can leadership and engineering teams view the same reasoning?",
    a: "Yes. CREAIOps generates a stakeholder-ready narrative and a technical reasoning trail from the same source of truth, so leadership and engineers stay aligned.",
  },
];

export default function AIOpsIncidentReasoning() {
  return (
    <SeoLandingLayout
      slug="aiops-incident-reasoning"
      metaTitle="AIOps Incident Reasoning Workspace | CREAIOps"
      metaDescription="CREAIOps is an AIOps incident reasoning workspace for SRE and IT operations. Multi-agent reasoning across app, infra, network, security, database, and business impact — with human-approved runbooks."
      h1="AIOps incident reasoning that turns signals into decisions"
      intro="CREAIOps is the reasoning layer on top of your AIOps and observability stack. Instead of more dashboards, on-call teams get ranked hypotheses, supporting evidence, and approval-ready next steps — generated from cross-domain agents that think the way your senior engineers do."
      problem={{
        title:
          "AIOps surfaces signals, but decisions still depend on a few human brains",
        body:
          "Even with mature observability, the hardest part of an incident is reasoning under pressure. Engineers must connect symptoms across services, weigh blast radius, decide what to try first, and explain it to non-technical stakeholders — all at once. That cognitive load doesn't scale.",
        points: [
          "Alert noise grows with every new service",
          "Correlated events don't always translate into actionable next steps",
          "Junior engineers wait for a senior to interpret signals during major incidents",
          "Post-incident reviews capture the outcome, not the reasoning",
        ],
      }}
      howWeHelp={{
        title:
          "Cross-domain reasoning agents that complement your AIOps stack",
        body:
          "CREAIOps dispatches specialist reasoning agents per incident — application, infrastructure, network, database, security, and business impact. They publish hypotheses, evidence, and confidence, and the platform combines them into a single ranked view your team can act on.",
        items: [
          {
            title: "Domain-aware reasoning, not just correlation",
            desc: "Agents reason from anonymized incident context, not from raw events alone — closer to how a senior on-call would think.",
          },
          {
            title: "Weighted consensus across perspectives",
            desc: "Hypotheses are scored and ranked so the team focuses on the most plausible causes first.",
          },
          {
            title: "Approval-ready runbook drafts",
            desc: "Suggested response, recovery, and follow-up steps in a single human-reviewable artefact.",
          },
          {
            title: "Auditable governance trail",
            desc: "Every approval and override is recorded with the reasoning context that informed it.",
          },
        ],
      }}
      benefits={[
        "Reduce cognitive load on senior on-call engineers",
        "Faster cross-team alignment during noisy bridges",
        "Higher-quality RCA artefacts for problem management",
        "Cleaner stakeholder communication during outages",
        "Better readiness for repeat incidents and seasonal traffic events",
      ]}
      useCase={{
        title:
          "An e-commerce platform uses CREAIOps to coordinate a checkout latency spike",
        body:
          "During a regional checkout slowdown, CREAIOps spins up reasoning across application, infrastructure, database, and network domains. The incident manager sees ranked hypotheses, a customer-facing draft, and a recommended action list — all reviewed by a human before any change.",
        steps: [
          "Anonymized incident brief is submitted into CREAIOps",
          "Domain agents reason in parallel and post hypotheses",
          "Consensus ranks the most likely root causes",
          "Stakeholder update and technical narrative are generated",
          "Runbook draft is approved by an authorised reviewer",
          "Audit trail is captured for the post-incident review",
        ],
      }}
      faq={FAQ}
      related={RELATED}
    />
  );
}
